import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useMemo, useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userEventRsvpStatusReportPage } from "../constants/reportPages"
import {
  exportUserEventRsvpAsCSV,
  getPaginationMeta,
  GetUserEventRsvpParams,
  useGetUserEventRsvpQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"

type UserEventRsvpDateFilters = Pick<
  GetUserEventRsvpParams,
  "start_date" | "end_date"
>

function formatEventColumnLabel(
  title: string | null,
  startDate: string | null
) {
  const eventTitle = title || "Untitled Event"

  if (!startDate) {
    return eventTitle
  }

  return `${eventTitle} (${new Date(startDate).toLocaleDateString()})`
}

function formatRsvpStatus(value: "accept" | "decline" | "tentative" | null) {
  if (!value) return "-"

  return value.charAt(0).toUpperCase() + value.slice(1)
}

function UserEventRsvpStatusPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Partial<UserEventRsvpDateFilters>
  >({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isLoading } = useGetUserEventRsvpQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "userName",
        headerName: "User Name",
        width: 220,
        sortable: false,
        valueGetter: (_value, row) => {
          const firstName = row.user?.first_name || ""
          const lastName = row.user?.last_name || ""
          return `${firstName} ${lastName}`.trim() || "-"
        },
      },
      {
        field: "userEmail",
        headerName: "Email",
        width: 260,
        sortable: false,
        valueGetter: (_value, row) => row.user?.email || "-",
      },
      {
        field: "organization",
        headerName: "Organization",
        width: 220,
        sortable: false,
        valueGetter: (_value, row) => row.organization?.label || "-",
      },
      {
        field: "total_org_events",
        headerName: "Org Events",
        width: 120,
        type: "number",
        sortable: false,
      },
      {
        field: "total_known_rsvp_events",
        headerName: "Known RSVP",
        width: 140,
        type: "number",
        sortable: false,
      },
    ]

    const eventDefinitions = Array.from(
      new Map(
        (data?.data || [])
          .flatMap((row) => row.events || [])
          .map((event) => [event.id, event])
      ).values()
    ).sort((a, b) => {
      const aDate = a.start_date ? new Date(a.start_date).getTime() : 0
      const bDate = b.start_date ? new Date(b.start_date).getTime() : 0

      if (aDate !== bDate) {
        return aDate - bDate
      }

      return (a.title || "").localeCompare(b.title || "")
    })

    const eventColumns: GridColDef[] = eventDefinitions.map((event) => ({
      field: `event:${event.id}`,
      headerName: formatEventColumnLabel(
        event.title ?? null,
        event.start_date ?? null
      ),
      width: 180,
      sortable: false,
      valueGetter: (_value, row) => {
        const rowEvent = (row.events || []).find(
          (candidate: {
            id: number
            rsvp_status: "accept" | "decline" | "tentative" | null
          }) => candidate.id === event.id
        )

        return formatRsvpStatus(rowEvent?.rsvp_status ?? null)
      },
    }))

    return [...baseColumns, ...eventColumns]
  }, [data?.data])

  const handleFilterChange = (
    field: "start_date" | "end_date",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const nextFilters: Partial<UserEventRsvpDateFilters> = {}
    const startMs = filters.start_date ? Date.parse(filters.start_date) : null
    const endMs = filters.end_date ? Date.parse(filters.end_date) : null

    if (startMs !== null && Number.isNaN(startMs)) {
      setValidationError("Start date is invalid.")
      return
    }

    if (endMs !== null && Number.isNaN(endMs)) {
      setValidationError("End date is invalid.")
      return
    }

    if (startMs !== null && endMs !== null && startMs > endMs) {
      setValidationError("Start date must be before end date.")
      return
    }

    if (startMs !== null) {
      nextFilters.start_date = new Date(startMs).toISOString()
    }

    if (endMs !== null) {
      nextFilters.end_date = new Date(endMs).toISOString()
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      start_date: "",
      end_date: "",
    })
    setAppliedFilters({})
  }

  const handleExportCSV = () => {
    const exportParams = {
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    }

    exportUserEventRsvpAsCSV(exportParams)
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userEventRsvpStatusReportPage.pageTitle}
        description={userEventRsvpStatusReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isLoading}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Event Date Range"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(value) =>
                handleFilterChange("start_date", value)
              }
              onEndDateChange={(value) => handleFilterChange("end_date", value)}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">
          Error loading RSVP status data. Please try again.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) => row.user.id}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default UserEventRsvpStatusPage
